import { Box, Divider, useMediaQuery } from '@mui/material';
import { useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Add, Visibility } from '@mui/icons-material';
import { IResponse, ISortDirection, ITestBuild } from '../../../models';
import { useQuery } from 'react-query';
import {
  Button,
  MobileTable,
  PageTitleWithButton,
  Pagination,
  SearchSection,
  Table
} from '../../../components';
import {
  SALES_PORTAL_PER_PAGE_OPTIONS,
  formatShortFriendlyDateWithTime,
  getEnvironment
} from '../../../helpers';
import { SalesContext, update } from '../../../context/sales';
import { getTestBuilds } from '../../../fetch';
import { TestBuildsFilters } from './test-builds-filters';
import { AddBuildModal } from '../clients';

const PAGE_NAME = 'test-builds';
export const TestBuildsList = () => {
  const history = useHistory();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const removeStickyHeader = useMediaQuery('(max-width: 1246px)');
  const [searchValue, setSearch] = useState<string>('');
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [filters, setFilters] = useState<{
    contractName?: string;
    neighborhoodId?: number | string;
    lotNumber?: number | string;
    planId?: number | string;
  } | null>({});
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string>('');
  const [selectedLotId, setSelectedLotId] = useState<string>('');

  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [isShowingAddTestBuildModal, setIsShowingAddTestBuildModal] = useState<boolean>(false);

  const { perPageSalesPortalValues, dispatchPerPage } = useContext(SalesContext);

  const currentPageState = perPageSalesPortalValues.find(
    state => state?.pageName === PAGE_NAME
  )?.itemsPerPage;
  const perPageContext = useMemo<number>(
    () => (currentPageState && currentPageState >= 25 ? currentPageState : 25),
    [currentPageState]
  );

  const [selectedSort, setSelectedSort] = useState<string>('BuildCreated');
  const [sortDirection, setSortDirection] = useState<{
    Neighborhood?: ISortDirection;
    LotNumber?: ISortDirection;
    Plan?: ISortDirection;
    BuildCreated?: ISortDirection;
    ContractName?: ISortDirection;
  }>({
    BuildCreated: 'Desc'
  });
  const [contractName, setContractName] = useState<string>('');

  const {
    isLoading,
    isFetching,
    data: buildsData,
    refetch: fetchTestBuilds
  } = useQuery<IResponse<ITestBuild[]>, Error>(
    ['getTestBuilds', page, perPageContext, selectedSort, sortDirection, filters],
    () =>
      getTestBuilds({
        page: page + 1,
        perPage: perPageContext,
        neighborhoodId: selectedNeighborhoodId,
        lotNumber: selectedLotId,
        sortBy: selectedSort,
        contractName: searchValue,
        sortDirection: sortDirection[selectedSort] as string,
        ...filters
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const handleView = (original: ITestBuild) => {
    history.push(
      `/sales/contracting/build-options/${original.buildId}?redirect=/sales/contracting/test-builds`
    );
  };

  const data = useMemo(() => buildsData?.records ?? [], [buildsData]);
  const columns = useMemo(() => {
    const handleClickColumn = (column: string) => {
      setSelectedSort(column);
      setSortDirection({
        ...sortDirection,
        [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
      });
    };
    return [
      {
        Header: 'Build Created',
        accessor: 'buildCreated',
        isServerSorted: selectedSort === 'buildCreated',
        isServerSortedDesc: sortDirection.BuildCreated === 'Desc',
        handleClickColumn: () => handleClickColumn('BuildCreated'),

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ITestBuild } };
        }) => {
          return (
            <>
              {original.buildCreated
                ? formatShortFriendlyDateWithTime(original.buildCreated)
                : null}
            </>
          );
        }
      },
      {
        Header: 'Neighborhood',
        accessor: 'neighborhoodName',
        isServerSorted: selectedSort === 'Neighborhood',
        isServerSortedDesc: sortDirection.Neighborhood === 'Desc',
        handleClickColumn: () => handleClickColumn('Neighborhood')
      },
      {
        Header: 'Lot',
        accessor: 'lotNumber',
        isServerSorted: selectedSort === 'LotNumber',
        isServerSortedDesc: sortDirection.LotNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('LotNumber')
      },
      {
        Header: 'Plan',
        accessor: 'plan',
        isServerSorted: selectedSort === 'plan',
        isServerSortedDesc: sortDirection.Plan === 'Desc',
        handleClickColumn: () => handleClickColumn('Plan')
      },
      {
        Header: 'Contract Name',
        accessor: 'contractName',
        isServerSorted: selectedSort === 'contractName',
        isServerSortedDesc: sortDirection.ContractName === 'Desc',
        handleClickColumn: () => handleClickColumn('ContractName')
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ITestBuild } };
        }) => {
          return (
            <Box display="flex">
              <Button
                id={`view-build-${original.buildId}`}
                type="button"
                color="primary"
                startIcon={<Visibility />}
                onClick={() => handleView(original)}
              >
                Details
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection, selectedSort]);

  return (
    <>
      <PageTitleWithButton
        pageTitleProps={{
          title: 'Test Builds'
        }}
        button={
          <Button
            id="add-test-build"
            color="primary"
            variant="contained"
            startIcon={<Add />}
            onClick={() => setIsShowingAddTestBuildModal(true)}
          >
            Add Test Build
          </Button>
        }
      />
      <TestBuildsFilters
        isLoading={isLoading || isFetching}
        selectedNeighborhoodId={selectedNeighborhoodId}
        selectedLotId={selectedLotId}
        setSelectedNeighborhoodId={setSelectedNeighborhoodId}
        setSelectedLotId={setSelectedLotId}
        setSearchValue={setSearch}
        searchValue={searchValue}
        handleSearch={(
          data: {
            contractName: string;
            selectedNeighborhoodId: string;
            selectedLotId: string;
          },
          clearSearch?: boolean
        ) => {
          setPage(0);
          setFilters({
            ...filters,
            contractName: searchValue,
            neighborhoodId: data?.selectedNeighborhoodId || undefined,
            lotNumber: data?.selectedLotId || undefined
          });
          setSearchedValue(searchValue);
          if (clearSearch || !searchValue) {
            setSearch('');
            setSearchedValue('');
            setFilters({
              ...filters,
              contractName: '',
              neighborhoodId: data?.selectedNeighborhoodId || undefined,
              lotNumber: data?.selectedLotId || undefined
            });
          }
        }}
        applyFilters={(clearFilters?: boolean) => {
          if (clearFilters) {
            setFilters({
              contractName: '',
              neighborhoodId: '',
              lotNumber: ''
            });
            setSearch('');
            setSearchedValue('');
          } else {
            setPage(0);
            setFilters({
              ...filters,
              contractName: searchValue || undefined,
              neighborhoodId: selectedNeighborhoodId || undefined,
              lotNumber: selectedLotId || undefined
            });
            setSearchedValue(searchValue);
          }
        }}
      />
      {searchedValue && (
        <SearchSection
          searchedValue={searchedValue}
          handleClearSearch={() => {
            setSearch('');
            setSearchedValue('');
            setPage(0);
            setFilters({
              ...filters,
              contractName: '',
              neighborhoodId: selectedNeighborhoodId || undefined,
              lotNumber: selectedLotId || undefined
            });
          }}
        />
      )}
      <Divider />
      <Table
        stickyHeaders={removeStickyHeader ? false : true}
        columns={columns}
        data={data}
        isLoading={isLoading || isFetching}
        hidePagination
        mobileProps={{
          fields: columns
            .filter(c => c.Header)
            .map(c => ({
              name: c.Header,
              accessor: c.accessor,
              Cell: c?.Cell
            })),
          handleView: (val: ITestBuild) => handleView(val),
          mobileSummaryAccessor: 'neighborhoodName'
        }}
        ResponsiveComponent={isMobile ? MobileTable : undefined}
      />
      {!isLoading && !isFetching && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPageContext}
          setPage={setPage}
          rowsPerPageOptions={SALES_PORTAL_PER_PAGE_OPTIONS}
          setRowsPerPage={itemsPerPage => {
            return dispatchPerPage(
              update({
                pageName: PAGE_NAME,
                environment: getEnvironment(),
                itemsPerPage: itemsPerPage
              })
            );
          }}
        />
      )}
      {isShowingAddTestBuildModal && (
        <AddBuildModal
          isTestBuild
          open={isShowingAddTestBuildModal}
          contractName={contractName}
          handleContractName={val => {
            setContractName(val);
          }}
          onClose={shouldRefetch => {
            if (shouldRefetch) {
              fetchTestBuilds();
            }
            setIsShowingAddTestBuildModal(false);
            setContractName('');
          }}
        />
      )}
    </>
  );
};
